import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram, faFirefox } from '@fortawesome/free-brands-svg-icons';
import { faWifi, faStar, faCrown, faCheck } from '@fortawesome/free-solid-svg-icons';
import DeliveryButtons from './DeliveryButtons';
import '../styles.css';
import '../index.css';
import { useShopInfo } from '../settings/SettingsContext';

const translations = {
  en: {
    connect: "CONNECT WITH US",
    facebook: <><FontAwesomeIcon icon={faFacebook} /> FACEBOOK</>,
    instagram: <><FontAwesomeIcon icon={faInstagram} /> INSTAGRAM</>,
    website: <><FontAwesomeIcon icon={faFirefox} /> WEBSITE</>,
    contact: "CONTACT INFO",
  },
  gr: {
    connect: "ΣΥΝΔΕΘΕΙΤΕ ΜΑΖΙ ΜΑΣ",
    facebook: <><FontAwesomeIcon icon={faFacebook} /> FACEBOOK</>,
    instagram: <><FontAwesomeIcon icon={faInstagram} /> INSTAGRAM</>,
    website: <><FontAwesomeIcon icon={faFirefox} /> ΙΣΤΟΣΕΛΙΔΑ</>,
    contact: "ΣΤΟΙΧΕΙΑ ΕΠΙΚΟΙΝΩΝΙΑΣ",
  },
};

interface SocialButtonsProps {
  selectedLanguage: 'gr' | 'en';
  /** Address + phone in the social grid (off where the caller already shows them, e.g. the branch info sheet). */
  showContact?: boolean;
}

const SocialButtons: React.FC<SocialButtonsProps> = ({ selectedLanguage, showContact = true }) => {
  const info = useShopInfo();
  const googleMapLink = info.googleMapLink ?? process.env.REACT_APP_GOOGLE_MAP_LINK ?? '';
  const addressText = info.addressText ?? process.env.REACT_APP_ADDRESS_TEXT ?? '';
  const phoneNumber = info.phoneNumber ?? process.env.REACT_APP_PHONE_NUMBER ?? '';
  const phoneLink = info.phoneLink ?? process.env.REACT_APP_PHONE_LINK ?? '';
  const facebookLink = info.fbLink ?? process.env.REACT_APP_FB_LINK ?? '';
  const instagramLink = info.instaLink ?? process.env.REACT_APP_INSTA_LINK ?? '';
  const websiteLink = info.websiteLink ?? process.env.REACT_APP_WEBSITE_LINK ?? '';
  const googleReviewLink = info.googleReviewLink ?? process.env.REACT_APP_GOOGLE_REVIEW_LINK ?? '';
  const contactLink = info.contactLink ?? process.env.REACT_APP_CONTACT_LINK ?? '';
  const wifiLink = info.wifiLink ?? process.env.REACT_APP_WIFI_LINK ?? '';
  const loyaltyLink = info.loyaltyLink ?? process.env.REACT_APP_LOYALTY_LINK ?? '';

  const t = translations[selectedLanguage];
  const [toastVisible, setToastVisible] = useState(false);

  const copyWifiPassword = () => {
    navigator.clipboard.writeText(wifiLink).then(() => {
      setToastVisible(true);
    }).catch(() => {});
  };

  useEffect(() => {
    if (toastVisible) {
      const timer = setTimeout(() => setToastVisible(false), 2500);
      return () => clearTimeout(timer);
    }
  }, [toastVisible]);

  const showAddress = showContact && googleMapLink && addressText;
  const showPhone = showContact && phoneLink && phoneNumber;
  const hasSocialButtons = facebookLink || instagramLink || websiteLink || contactLink || showAddress || showPhone;

  return (
    <>
      {/* Toast notification */}
      <div className={`toast ${toastVisible ? 'toast--visible' : ''}`}>
        <FontAwesomeIcon icon={faCheck} className="toast__icon" />
        <span>{selectedLanguage === 'en' ? 'Wi-Fi password copied!' : 'Ο κωδικός Wi-Fi αντιγράφηκε!'}</span>
      </div>

      {/* Action cards row */}
      {(wifiLink || googleReviewLink) && (
        <div className="action-cards">
          {wifiLink && (
            <button className="action-card" onClick={copyWifiPassword}>
              <div className="action-card__icon action-card__icon--wifi">
                <FontAwesomeIcon icon={faWifi} />
              </div>
              <div className="action-card__text">
                <strong>{selectedLanguage === 'en' ? 'FREE Wi-Fi' : 'ΔΩΡΕΑΝ Wi-Fi'}</strong>
                <span>{selectedLanguage === 'en' ? 'Tap to connect' : 'Πατήστε για σύνδεση'}</span>
              </div>
            </button>
          )}
          {googleReviewLink && (
            <a href={googleReviewLink} target="_blank" rel="noopener noreferrer" className="action-card">
              <div className="action-card__icon action-card__icon--review">
                <FontAwesomeIcon icon={faStar} />
              </div>
              <div className="action-card__text">
                <strong>{selectedLanguage === 'en' ? 'Leave a Review' : 'Αφήστε Κριτική'}</strong>
                <span>Google</span>
              </div>
            </a>
          )}
        </div>
      )}

      {/* Loyalty card */}
      {loyaltyLink && (
        <a href={loyaltyLink} target="_blank" rel="noopener noreferrer" className="loyalty-card">
          <div className="loyalty-card__shimmer" />
          <div className="loyalty-card__content">
            <div className="loyalty-card__icon">
              <FontAwesomeIcon icon={faCrown} />
            </div>
            <div className="loyalty-card__text">
              <strong>{selectedLanguage === 'en' ? 'Join our Loyalty Program' : 'Εφαρμογή Επιβράβευσης'}</strong>
              <span>{selectedLanguage === 'en' ? 'You earn while you trust us every day' : 'Κερδίζετε ενώ μας εμπιστεύεστε καθημερινά'}</span>
            </div>
            <div className="loyalty-card__arrow">→</div>
          </div>
        </a>
      )}

      <DeliveryButtons selectedLanguage={selectedLanguage} />

      {hasSocialButtons && (
        <div className="container">
          <p className="connectText">{t.connect}</p>
          <div className="button-grid">
            {facebookLink && (
              <a href={facebookLink} target="_blank" rel="noopener noreferrer" className="button social-button facebook">
                {t.facebook}
              </a>
            )}
            {instagramLink && (
              <a href={instagramLink} target="_blank" rel="noopener noreferrer" className="button social-button instagram">
                {t.instagram}
              </a>
            )}
            {websiteLink && (
              <a href={websiteLink} target="_blank" rel="noopener noreferrer" className="button social-button website">
                {t.website}
              </a>
            )}
            {contactLink && (
              <a href={contactLink} target="_blank" rel="noopener noreferrer" className="button social-button contact">
                {t.contact}
              </a>
            )}
            {showAddress && (
              <div className='address-phone'>
                <svg viewBox="0 0 384 512" width="15" fill="currentColor">
                  <path d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z" />
                </svg>
                <a href={googleMapLink} target="_blank" rel="noopener noreferrer" className="address-text">
                  {addressText}
                </a>
              </div>
            )}
            {showPhone && (
              <div className='address-phone'>
                <svg viewBox="0 0 64 64" width="20" fill="currentColor">
                  <path d="M58.9 47l-10.4-6.8a4.8 4.8 0 0 0-6.5 1.3c-2.4 2.9-5.3 7.7-16.2-3.2S19.6 24.4 22.5 22a4.8 4.8 0 0 0 1.3-6.5L17 5.1c-.9-1.3-2.1-3.4-4.9-3S2 6.6 2 15.6s7.1 20 16.8 29.7S39.5 62 48.4 62s13.2-8 13.5-10-1.7-4.1-3-5z" />
                </svg>
                <a href={phoneLink} className="phone-text">
                  {phoneNumber}
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SocialButtons;
